import { useState, useRef } from 'react'
import { apiFetch, API_BASE, storedToken } from '../lib/api'
import { DIETS, TIERS, WHO_WILL_PAY_OPTIONS, inputCls } from '../lib/constants'

function Field({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
      <input type="text" value={value} onChange={onChange} className={inputCls} />
    </div>
  )
}

export default function Settings({ currentUser, loadUsers }) {
  const [form, setForm] = useState({
    name:          currentUser?.name || '',
    bio:           currentUser?.bio || '',
    area:          currentUser?.location?.area || '',
    city:          currentUser?.location?.city || '',
    dietaryType:   currentUser?.preferences?.dietaryType || 'VEG',
    budgetVibe:    currentUser?.preferences?.budgetVibe || 'SIMPLE',
    whoWillPay:    currentUser?.preferences?.whoWillPay || '',
    interestsInput: (currentUser?.preferences?.interests || []).join(', ')
  })
  const [avatarFile, setAvatarFile]       = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [saving, setSaving]               = useState(false)
  const [saved, setSaved]                 = useState(false)
  const [error, setError]                 = useState('')
  const fileInputRef                      = useRef(null)

  if (!currentUser) return <p className="text-sm text-gray-600 mt-6">Please log in first.</p>

  function handleAvatarChange(e) {
    const file = e.target.files[0]
    if (!file) return
    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
  }

  async function save() {
    setSaving(true); setError('')
    let profilePicture = undefined

    if (avatarFile) {
      const fd = new FormData()
      fd.append('image', avatarFile)
      const uploadRes = await fetch(`${API_BASE}/auth/upload-avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${storedToken()}` },
        body: fd
      })
      if (!uploadRes.ok) {
        const d = await uploadRes.json()
        setError(d.message || 'Avatar upload failed.')
        setSaving(false)
        return
      }
      const { url } = await uploadRes.json()
      profilePicture = url
    }

    const res = await apiFetch(`/users/${currentUser.id}/profile`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: form.name,
        bio: form.bio, area: form.area, city: form.city,
        dietaryType: form.dietaryType, budgetVibe: form.budgetVibe, whoWillPay: form.whoWillPay,
        interests: form.interestsInput.split(',').map(s => s.trim()).filter(Boolean),
        ...(profilePicture !== undefined && { profilePicture })
      })
    })
    setSaving(false)
    if (res.ok) {
      setSaved(true)
      setAvatarFile(null)
      loadUsers()
      setTimeout(() => setSaved(false), 2000)
    } else {
      const d = await res.json()
      setError(d.message)
    }
  }

  const avatarSrc = avatarPreview || currentUser.profilePicture

  return (
    <section>
      <div className="fade-up glass-card rounded-3xl border border-white/60 p-6 shadow-xl">
        <h2 className="title-font text-2xl font-bold text-gray-900 mb-6">Settings</h2>
        {error && <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
        <div className="space-y-4 max-w-lg">

          {/* Profile picture */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Profile Picture</label>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full overflow-hidden bg-orange-100 flex items-center justify-center shrink-0">
                {avatarSrc
                  ? <img src={avatarSrc} alt="avatar" className="h-full w-full object-cover" />
                  : <span className="text-2xl font-bold text-orange-400">{currentUser.name?.[0]?.toUpperCase()}</span>
                }
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-full border border-orange-300 px-4 py-1.5 text-sm font-medium text-orange-600 hover:bg-orange-50"
              >
                Change photo
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleAvatarChange} />
            </div>
          </div>

          <Field label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <Field label="Bio" value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
            <Field label="Area" value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Dietary Type</label>
            <select value={form.dietaryType} onChange={e => setForm(f => ({ ...f, dietaryType: e.target.value }))} className={inputCls}>
              {DIETS.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Budget Vibe</label>
            <select value={form.budgetVibe} onChange={e => setForm(f => ({ ...f, budgetVibe: e.target.value }))} className={inputCls}>
              {TIERS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Who will pay?</label>
            <select value={form.whoWillPay} onChange={e => setForm(f => ({ ...f, whoWillPay: e.target.value }))} className={inputCls}>
              <option value="">Select an option</option>
              {WHO_WILL_PAY_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
          <Field label="Interests (comma separated)" value={form.interestsInput} onChange={e => setForm(f => ({ ...f, interestsInput: e.target.value }))} />
          <button onClick={save} disabled={saving} className="rounded-full bg-orange-500 px-6 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">
            {saving ? 'Saving…' : saved ? '✅ Saved!' : 'Save Changes'}
          </button>
        </div>
      </div>
    </section>
  )
}
